'use client'

import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import SimpleBlockContent from '../../SimpleBlockContent'
import Text from '../../Text'
import { toPath } from '../../../utils/urls'
import SmartLink from '../../SmartLink'
import { http } from '../../../utils/clientHttp'
import { useRegionOffers } from '../../RegionOffers'
import { useCartStore } from '../../../store/cartStore'
import { useSessionStore } from '../../../store/sessionStore'
import { paymentFlowStatus } from '../../../utils/payments/payments'
import checkoutStyles from '../Products/ProductCheckoutPage/styles.module.css'
import { $neutralGray70 } from '../../../styles/colors/primitives'
import { CircularProgress } from '@mui/material'
import sendMetaPixelEvent, { buildPurchaseEvent } from '../../../utils/metaPixel'
import { pushEcommerceEvent, toGA4Item } from '../../../utils/dataLayer'
import { catalogItemId } from '../../../utils/catalogId'
import axios from 'axios'

// The order-confirmation querystring (region / provider / relatedPaymentId /
// discountCode) is entry-time URL state — read it straight from the App-Router
// URL in the post-mount effects below, NOT from Overmind (`state.query`, migrated
// off the store in Phase C2 part 3). Guarded for SSR (`window` undefined); every
// caller runs inside a client-only useEffect, so the URL is always available.
function readQueryParams() {
  if (typeof window === 'undefined') return {}
  return Object.fromEntries(new URLSearchParams(window.location.search))
}

// Build { items, value, currency } from the AUTHORITATIVE backend invoice
// (paymentState.invoice.items[]). Preferred over the Zustand cart snapshot,
// which is racy on the order-confirmation page: the visitor arrives via a
// Stripe redirect back to vrs.racing, and the Zustand cart hydrates
// asynchronously (skipHydration:true — see store/cartStore.js). Reading the
// cart before <CartStoreHydrator/> commits gets `{}` -> empty items / $0 value
// -> corrupts GA4 revenue AND poisons Google Ads Enhanced Conversions bid
// optimization (was firing $0 conversions).
//
// Invoice lines carry `itemType` on real product rows (undefined on synthetic
// shipping / tax / discount lines — see utils/invoiceItems.js). Filter to
// itemType-having lines for the GA4 items array; the grand total comes from
// invoice.total (which InvoicePreview also renders — same source of truth).
function buildPurchaseFromInvoice(paymentState, offers) {
  const invoice = paymentState && paymentState.invoice
  if (!invoice || !Array.isArray(invoice.items)) return null
  const productLines = invoice.items.filter(line => line && line.itemType)
  if (!productLines.length) return null
  const items = productLines.map(line => {
    const shopItem = offers && offers.find(s => s.type === line.itemType)
    const priceCents = line.amount && line.amount.priceCents
    const quantity = line.quantity || 1
    const unitPrice = priceCents ? priceCents / 100 / quantity : 0
    return {
      // The invoice line's itemType IS the catalog key (utils/catalogId.js).
      item_id: catalogItemId(line.itemType),
      item_name: (shopItem && shopItem.name) || line.description,
      price: unitPrice,
      quantity,
    }
  })
  const total = invoice.total
  const value = total && total.priceCents != null
    ? total.priceCents / 100
    : items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0)
  const currency =
    (total && total.currency) ||
    (invoice.items[0] && invoice.items[0].amount && invoice.items[0].amount.currency)
  return { items, value, currency }
}

async function firePurchaseDataLayer(paymentState, cart, shopItems, query) {
  // PRIMARY: backend invoice (see buildPurchaseFromInvoice comment).
  const fromInvoice = buildPurchaseFromInvoice(paymentState, shopItems)
  let items, productValue, currency
  if (fromInvoice) {
    items = fromInvoice.items
    productValue = fromInvoice.value
    currency = fromInvoice.currency
  } else {
    // FALLBACK: pre-clear cart snapshot. Kept for flows that don't return an
    // invoice (older subscription paths, non-invoice-shaped responses).
    const cartIds = cart ? Object.keys(cart) : []
    items = cartIds
      .map(id => {
        const shopItem = shopItems && shopItems.find(i => i.id === id)
        return shopItem ? toGA4Item(shopItem, cart[id]) : null
      })
      .filter(Boolean)
    productValue = items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0)
  }
  // Tax + shipping — the backend PaymentFlowStateDto.paymentTotal is a PriceDto
  // (priceCents + currency) with no tax/shipping breakdown, so derive them from
  // the invoice: the "Shipping & Handling" line has itemType=null, tax comes
  // off any product line's tax percentage applied to its amount.
  const total = paymentState.paymentTotal || {}
  const invoiceForTax = paymentState && paymentState.invoice
  const shippingLine = invoiceForTax && Array.isArray(invoiceForTax.items)
    ? invoiceForTax.items.find(l => l && !l.itemType && /shipping/i.test(l.description || ''))
    : null
  const shipping = shippingLine && shippingLine.amount && shippingLine.amount.priceCents != null
    ? shippingLine.amount.priceCents / 100
    : undefined
  // Tax = sum of line-level tax where declared as non-inclusive (our current
  // backend flags tax objects with inclusive:false and a percentage).
  const tax = invoiceForTax && Array.isArray(invoiceForTax.items)
    ? invoiceForTax.items.reduce((sum, l) => {
        if (!l || !l.amount || !l.tax || l.tax.inclusive) return sum
        const pct = l.tax.percentage || 0
        const lineTaxCents = Math.round((l.amount.priceCents || 0) * pct / (100 + pct))
        return sum + lineTaxCents
      }, 0) / 100
    : undefined
  if (!currency) currency = total.currency || (items[0] && items[0].currency)

  // Customer identity: PRIMARY source is the backend PaymentFlowStateDto fields
  // shipped 2026-07-28 (billingEmail + billingAddress + shippingAddress). The
  // old customerEmail / customer.email / customer.address paths are kept as
  // second-tier fallbacks — they were never actually populated on the response
  // (dead code), but the fallbacks are cheap and future-proof for a schema
  // change. AddressDto uses `name` (single field, not first_name/last_name),
  // `line1`/`line2` (not street/address1), and `phoneNumber` (not phone).
  const billingAddr = paymentState.billingAddress || null
  const shippingAddr = paymentState.shippingAddress || null
  const address = shippingAddr || billingAddr || {}
  const email = paymentState.billingEmail
    || (billingAddr && billingAddr.email)
    || (shippingAddr && shippingAddr.email)
    || paymentState.customerEmail
    || (paymentState.customer && paymentState.customer.email)
  const phone = (billingAddr && billingAddr.phoneNumber)
    || (shippingAddr && shippingAddr.phoneNumber)
    || paymentState.customerPhone
    || (paymentState.customer && paymentState.customer.phone)

  const payload = {
    transaction_id: query && query.relatedPaymentId,
    value: productValue,
    tax,
    shipping,
    currency,
    coupon: paymentState.discountCode || (query && query.discountCode),
    items,
  }
  if (email || phone || address.email) {
    payload.user_data = {
      email: email || address.email,
      phone_number: phone,
      address: {
        // AddressDto ships a single `name` field, not first/last split; forward
        // as-is so GA4 Enhanced Conversions can hash it. Consumers that need
        // first/last can split later.
        name: address.name,
        street: address.line1,
        street2: address.line2,
        city: address.city,
        region: address.state,
        postal_code: address.postalCode,
        country: address.country,
      },
    }
  }

  if (email) {
    try {
      const insights = await axios.get(`/api/customer/getCustomerInsights?email=${encodeURIComponent(email)}`)
      const data = insights && insights.data
      if (data && typeof data.new_customer === 'boolean') payload.new_customer = data.new_customer
      if (data && typeof data.customer_lifetime_value === 'number') payload.customer_lifetime_value = data.customer_lifetime_value
    } catch (e) {
      // Silent — customer insights lookup must not block the purchase event.
    }
  }

  pushEcommerceEvent('purchase', payload)
}

// sessionStorage key template — keyed by transaction_id (Stripe
// relatedPaymentId) so the same visitor reloading / hitting back / bfcache-
// restoring this page cannot fire duplicate `purchase` events into GA4 / Meta
// / Ads. The React `purchaseEventSent` ref is per-mount only; sessionStorage
// survives all in-tab navigations and clears on tab close (so a genuinely
// separate later session would still fire — desired for legitimate re-orders).
//
// GA4 dedupes purchases by transaction_id server-side already, but Meta uses
// event_id which regenerates on each fire, so a reload would double-count
// server-side + browser-side. Google Ads Enhanced Conversions is also
// event_id sensitive. Cheap client-side guard eliminates all three risks.
const PURCHASE_EVENT_FIRED_STORAGE_PREFIX = 'vrs_purchase_event_fired:'

function purchaseAlreadyFiredThisSession(transactionId) {
  if (!transactionId || typeof window === 'undefined') return false
  try {
    return !!window.sessionStorage.getItem(PURCHASE_EVENT_FIRED_STORAGE_PREFIX + transactionId)
  } catch (_e) {
    return false
  }
}

function markPurchaseFiredThisSession(transactionId) {
  if (!transactionId || typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(PURCHASE_EVENT_FIRED_STORAGE_PREFIX + transactionId, '1')
  } catch (_e) {
    // Silent — quota exceeded / private-mode restrictions must not break tracking.
  }
}

function OrderConfirmationPage({ orderUrl, title, subtitle, text, buttons, socialMediaInfo }) {
  // Offers migrated off Overmind (Phase D1) → the RegionOffers seam; used to
  // resolve the purchased cart ids for the purchase pixel / dataLayer.
  const { offers } = useRegionOffers()

  const [paymentState, setPaymentState] = useState(null)
  const [shoppingCartBeforeDeletion, setShoppingCartBeforeDeletion] = useState(null)
  const purchaseEventSent = useRef(false)

  useEffect(() => {
    const query = readQueryParams()
    http.get(`/api/payments/getPaymentFlowState?region=${query.region}&provider=${query.provider}&relatedPaymentId=${query.relatedPaymentId}`)
      .then(res => setPaymentState(res.data))
      .catch(e => setPaymentState({ status: paymentFlowStatus.FINISHED_FAILURE }))
  }, [])

  // Snapshot the Zustand cart AFTER hydration commits and BEFORE the reset —
  // this feeds the purchase pixel/dataLayer as a fallback for the case where
  // the backend PaymentFlowState response is missing an invoice (older records,
  // subscription/coaching flows). The primary path uses paymentState.invoice.
  //
  // Waiting for _hasHydrated fixes the race that was making cart snapshots
  // empty on the Stripe-redirect return: <CartStoreHydrator/> rehydrates
  // asynchronously in a mount effect, so the first render sees cart={},
  // and snapshotting right after the payment-state fetch usually lost the
  // race and grabbed an empty cart -> items:[] + value:0 downstream.
  const cartHasHydrated = useCartStore((s) => s._hasHydrated)
  useEffect(() => {
    if (!cartHasHydrated) return
    if (shoppingCartBeforeDeletion !== null) return
    setShoppingCartBeforeDeletion(useCartStore.getState().cart)
    useCartStore.getState().reset()
    useSessionStore.getState().resetDiscountCode()
  }, [cartHasHydrated, shoppingCartBeforeDeletion])

  useEffect(() => {
    if (purchaseEventSent.current) return
    if (!paymentState || paymentState.status === paymentFlowStatus.FINISHED_FAILURE) return
    const query = readQueryParams()
    // Idempotency guard (session-scoped): if a purchase event was already fired
    // for this transaction_id in this browser tab session (reload, back/forward,
    // bfcache restore all count as the same session), skip. React ref guard
    // above only survives within a single mount — sessionStorage survives all
    // in-tab navigations. Skips BOTH the Meta Pixel Purchase and the GA4
    // dataLayer push, so browser + CAPI + Ads all agree.
    if (purchaseAlreadyFiredThisSession(query.relatedPaymentId)) {
      purchaseEventSent.current = true
      return
    }
    purchaseEventSent.current = true
    markPurchaseFiredThisSession(query.relatedPaymentId)

    // Meta Pixel value/items sourced from the AUTHORITATIVE backend invoice —
    // same rationale as firePurchaseDataLayer above (Zustand cart is racy on
    // the Stripe-redirect return; without this fix Meta was seeing $0 purchases
    // when the cart snapshot lost the hydration race).
    const cart = shoppingCartBeforeDeletion || {}
    const cartIds = Object.keys(cart)
    const fromInvoice = buildPurchaseFromInvoice(paymentState, offers)
    let productValue, currency, contentIds, numItems
    if (fromInvoice) {
      productValue = fromInvoice.value
      currency = fromInvoice.currency
      contentIds = fromInvoice.items.map(i => i.item_id)
      numItems = fromInvoice.items.reduce((sum, i) => sum + (i.quantity || 1), 0)
    } else {
      const items = cartIds
        .map(id => {
          const shopItem = offers && offers.find(i => i.id === id)
          return shopItem ? toGA4Item(shopItem, cart[id]) : null
        })
        .filter(Boolean)
      productValue = items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0)
      const total = paymentState.paymentTotal || {}
      currency = total.currency || (items[0] && items[0].currency)
      contentIds = cartIds
      numItems = cartIds.length
    }

    // user_data for Meta Advanced Matching — hashed server-side by CAPI endpoint.
    // Reads the PaymentFlowStateDto billingEmail/billingAddress/shippingAddress
    // (shipped 2026-07-28) with the legacy paths kept as second-tier fallbacks.
    const billingAddr = paymentState.billingAddress || null
    const shippingAddr = paymentState.shippingAddress || null
    const address = shippingAddr || billingAddr || (paymentState.customer && paymentState.customer.address) || {}
    const email = paymentState.billingEmail
      || (billingAddr && billingAddr.email)
      || (shippingAddr && shippingAddr.email)
      || paymentState.customerEmail
      || (paymentState.customer && paymentState.customer.email)
      || address.email
    const phone = (billingAddr && billingAddr.phoneNumber)
      || (shippingAddr && shippingAddr.phoneNumber)
      || paymentState.customerPhone
      || (paymentState.customer && paymentState.customer.phone)
    const userData = (email || phone) ? {
      email: email || undefined,
      phone: phone || undefined,
    } : undefined

    sendMetaPixelEvent(buildPurchaseEvent({
      value: productValue,
      currency,
      ids: contentIds,
      numItems,
      userData,
      transactionId: query.relatedPaymentId,
    }))
    firePurchaseDataLayer(paymentState, shoppingCartBeforeDeletion, offers, query)
  }, [paymentState, shoppingCartBeforeDeletion])

  if (!paymentState) {
    return (
      <div className={checkoutStyles.loading} style={{ color: $neutralGray70 }}>
        <CircularProgress color="inherit"/>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.allTextWrapper}>
            {
              (() => {
                if (paymentState.status === paymentFlowStatus.FINISHED_FAILURE) {
                  return (
                    <>
                      <div>
                        <Text type="heading-1">Something went wrong</Text>
                        <Text type="heading-4">{paymentState.userFriendlyMessage}</Text>
                      </div>
                    </>
                  )
                } else {
                  return (
                    <>
                      <div>
                        <Text type="heading-1">Thank you!</Text>
                        <Text type="heading-4">{paymentState.userFriendlyMessage}</Text>
                      </div>
                      <SimpleBlockContent blocks={text} noSpace/>
                      <a className={styles.orderUrl} href={paymentState.orderUrl}><Text type="large">You can review your order here</Text></a>
                    </>
                  )
                }
              }) ()
            }
        </div>
        <div className={styles.buttonsWrapper}>
          {
            buttons && buttons.map(b => (
              <SmartLink href={toPath(b.routeOrUrl)} key={b.title} className={styles.button}>
                {/* task #67: b.icon is now a ResolvedImage descriptor */}
                <img className={styles.icon} src={b.icon?.src} alt="" width={32} height={32}/>
                <Text type="heading-7">{b.title}</Text>
              </SmartLink>
          ))}
        </div>
        <div className={styles.socials}>
          <div className={styles.socialsTitle}>
            <div className={styles.socialsTitleLine}/>
            <div className={styles.socialsTitleText}><Text type="heading-6">Follow us</Text></div>
            <div className={styles.socialsTitleLine}/>
          </div>
          <div className={styles.iconsWrapper}>
            {
              socialMediaInfo.map(s => (
                <a href={s.link} target={'_blank'} rel="noreferrer" key={s._key}>
                  {/* task #67: s.icon is now a ResolvedImage descriptor */}
                  <img className={styles.icon} src={s.icon?.src} alt="" width={32} height={32}/>
                </a>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderConfirmationPage
